import React, { useEffect, useMemo, useState } from 'react';
import { format, addHours, addDays, addMonths } from 'date-fns';
import type { AxiosRequestConfig } from 'axios';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Filter, Calendar, RefreshCw, GitBranch, Activity, Download } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Legend
} from 'recharts';

interface BranchItem {
  id: string;
  name: string;
  company?: { name: string };
}

type Period = 'hour' | 'day' | 'week' | 'month' | 'year' | 'custom';

interface QueueSeriesPoint {
  bucket: string;
  bucketLabel?: string;
  totalUploaded: string | number;
  totalDownloaded: string | number;
  totalTraffic: string | number;
  samples: number;
}

interface QueueSeriesResponse {
  period: string;
  series: QueueSeriesPoint[];
}

interface BranchSummaryRow {
  branchId: string;
  branchName: string;
  totalUploaded: string | number;
  totalDownloaded: string | number;
  totalTraffic: string | number;
  samples: number;
}

type ReportsRequestConfig = AxiosRequestConfig & {
  preserveSessionOnAuthError?: boolean;
};

const Reports: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [queues, setQueues] = useState<string[]>([]);
  const [series, setSeries] = useState<QueueSeriesResponse | null>(null);
  const [branchSummary, setBranchSummary] = useState<BranchSummaryRow[]>([]);
  const [totalsByPeriod, setTotalsByPeriod] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [accumulate, setAccumulate] = useState(false);

  const [period, setPeriod] = useState<Period>('day');
  const [branchId, setBranchId] = useState('');
  const [queueName, setQueueName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const computedRange = useMemo(() => {
    if (period === 'custom') {
      return { start: startDate, end: endDate };
    }
    const now = new Date();
    const start = new Date(now);

    if (period === 'hour') {
      start.setHours(now.getHours() - 24, 0, 0, 0);
    }
    if (period === 'day') {
      start.setDate(now.getDate() - 30);
      start.setHours(0, 0, 0, 0);
    }
    if (period === 'week') {
      start.setDate(now.getDate() - 7);
      start.setHours(0, 0, 0, 0);
    }
    if (period === 'month') {
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
    }
    if (period === 'year') {
      start.setMonth(now.getMonth() - 12, 1);
      start.setHours(0, 0, 0, 0);
    }

    return {
      start: start.toISOString(),
      end: now.toISOString()
    };
  }, [period, startDate, endDate]);

  const fetchFilters = async () => {
    try {
      const branchesRes = await api.get('/branches', {
        preserveSessionOnAuthError: true
      } as ReportsRequestConfig);
      setBranches(branchesRes.data || []);
    } catch (error) {
      toast.error('Failed to load filters');
    }
  };

  const fetchQueues = async (selectedBranchId: string) => {
    if (!selectedBranchId) {
      setQueues([]);
      setQueueName('');
      return;
    }
    try {
      const queuesRes = await api.get('/reports/queues', {
        preserveSessionOnAuthError: true,
        params: { branchId: selectedBranchId || undefined }
      } as ReportsRequestConfig);
      setQueues(queuesRes.data || []);
    } catch (error) {
      toast.error('Failed to load queues');
    }
  };

  const fetchReports = async () => {
    if (!branchId) {
      setSeries(null);
      setBranchSummary([]);
      setTotalsByPeriod({});
      setLoading(false);
      setRefreshing(false);
      return;
    }

    setRefreshing(true);
    try {
      const modeForSeries: 'delta' = 'delta';
      const modeForCards: 'delta' = 'delta';
      const params: any = {
        branchId: branchId || undefined,
        queueName: queueName || undefined,
        startDate: computedRange.start || undefined,
        endDate: period === 'custom' ? computedRange.end || undefined : undefined,
        period: period === 'custom' ? 'day' : period,
        mode: modeForSeries
      };
      const requestConfig: ReportsRequestConfig = {
        preserveSessionOnAuthError: true
      };

      const [seriesRes, branchRes, totalsRes] = await Promise.all([
        api.get('/reports/queue-series', { ...requestConfig, params }),
        api.get('/reports/queue-branch-summary', {
          ...requestConfig,
          params: {
            branchId: branchId || undefined,
            queueName: queueName || undefined,
            startDate: computedRange.start || undefined,
            endDate: period === 'custom' ? computedRange.end || undefined : undefined,
            mode: modeForCards
          }
        }),
        api.get('/reports/queue-totals', {
          ...requestConfig,
          params: {
            branchId: branchId || undefined,
            queueName: queueName || undefined,
            now: new Date().toISOString()
          }
        })
      ]);

      setSeries(seriesRes.data);
      setBranchSummary(branchRes.data || []);

      const totalsPayload = totalsRes.data || {};
      setTotalsByPeriod({
        hour: toNumber(totalsPayload.hour),
        day: toNumber(totalsPayload.day),
        month: toNumber(totalsPayload.month),
        year: toNumber(totalsPayload.year)
      });
    } catch (error: any) {
      if (error.response?.status === 401) {
        toast.error('Nao foi possivel carregar o report agora, mas sua sessao foi mantida.');
      } else {
        toast.error('Failed to load reports');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchQueues(branchId);
  }, [branchId]);

  useEffect(() => {
    setQueueName('');
  }, [branchId]);

  useEffect(() => {
    fetchReports();
  }, [period, branchId, queueName, computedRange.start, computedRange.end]);

  const periodLabel = {
    hour: 'Ultimas 24 horas (por hora)',
    day: 'Ultimos 30 dias (dia a dia)',
    week: 'Ultimos 7 dias',
    month: 'Mes atual (dia a dia)',
    year: 'Ultimos 12 meses',
    custom: 'Periodo customizado'
  }[period];

  const toNumber = (value: string | number) => {
    if (typeof value === 'number') return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatBytes = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const idx = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
    const scaled = value / Math.pow(1024, idx);
    const digits = scaled >= 10 || idx === 0 ? 0 : 1;
    return `${scaled.toFixed(digits)} ${units[idx]}`;
  };

  const totalTraffic = series?.series?.reduce((acc, row) => acc + toNumber(row.totalTraffic), 0) ?? 0;
  const totalUploaded = series?.series?.reduce((acc, row) => acc + toNumber(row.totalUploaded), 0) ?? 0;
  const totalDownloaded = series?.series?.reduce((acc, row) => acc + toNumber(row.totalDownloaded), 0) ?? 0;
  const totalSamples = series?.series?.reduce((acc, row) => acc + (row.samples || 0), 0) ?? 0;

  const formatBucket = (value: string) => {
    const date = new Date(value);
    if (series?.period === 'hour') return format(date, 'HH:00');
    if (series?.period === 'day') return format(date, 'dd/MM');
    if (series?.period === 'month') return format(date, 'MM/yyyy');
    if (series?.period === 'year') return format(date, 'yyyy');
    return format(date, 'dd/MM HH:mm');
  };

  const formatBucketLabel = (label: string) => {
    if (!label || label.length < 10) return label;
    if (series?.period === 'hour') return label.slice(11, 13) + ':00';
    if (series?.period === 'day') return `${label.slice(8, 10)}/${label.slice(5, 7)}`;
    if (series?.period === 'month') return `${label.slice(5, 7)}/${label.slice(0, 4)}`;
    if (series?.period === 'year') return label.slice(0, 4);
    return label;
  };

  const normalizeBucketDate = (value: Date) => {
    const normalized = new Date(value);
    if (series?.period === 'hour') {
      normalized.setMinutes(0, 0, 0);
    } else if (series?.period === 'day') {
      normalized.setHours(0, 0, 0, 0);
    } else {
      normalized.setDate(1);
      normalized.setHours(0, 0, 0, 0);
    }
    return normalized;
  };

  const rawChartData = useMemo(() => {
    const aggregated = new Map<
      string,
      {
        bucket: string;
        key: string;
        label: string;
        upload: number;
        download: number;
        total: number;
        samples: number;
      }
    >();

    for (const row of series?.series || []) {
      const bucketDate = normalizeBucketDate(new Date(row.bucket));
      const key = String(bucketDate.getTime());
      const current = aggregated.get(key) || {
        bucket: bucketDate.toISOString(),
        key,
        label: formatBucket(bucketDate.toISOString()),
        upload: 0,
        download: 0,
        total: 0,
        samples: 0
      };

      current.upload += toNumber(row.totalUploaded);
      current.download += toNumber(row.totalDownloaded);
      current.total += toNumber(row.totalTraffic);
      current.samples += row.samples || 0;
      aggregated.set(key, current);
    }

    return Array.from(aggregated.values()).sort((a, b) => Number(a.key) - Number(b.key));
  }, [series]);

  const fillTimeline = () => {
    if (!series) return rawChartData;
    const endIso = period === 'custom' ? computedRange.end : new Date().toISOString();
    const start = computedRange.start ? new Date(computedRange.start) : null;
    const end = endIso ? new Date(endIso) : null;
    if (!start || !end) return rawChartData;

    const startLocal = start;
    const endLocal = end;

    const byKey = new Map(rawChartData.map((row) => [row.key, row]));
    const filled: typeof rawChartData = [];

    const step =
      series.period === 'hour'
        ? (date: Date) => addHours(date, 1)
        : series.period === 'day'
        ? (date: Date) => addDays(date, 1)
        : series.period === 'month'
        ? (date: Date) => addMonths(date, 1)
        : (date: Date) => addMonths(date, 1);

    let cursor = startLocal;
    while (cursor <= endLocal) {
      const bucketDate = normalizeBucketDate(cursor);
      const key = String(bucketDate.getTime());
      const existing = byKey.get(key);
      filled.push(
        existing || {
          bucket: bucketDate.toISOString(),
          key,
          label: formatBucket(bucketDate.toISOString()),
          upload: 0,
          download: 0,
          total: 0,
          samples: 0
        }
      );
      cursor = step(cursor);
    }

    return filled;
  };

  const chartDataFilled = fillTimeline();

  const chartData = accumulate
    ? chartDataFilled.reduce((acc: any[], row) => {
        const last = acc[acc.length - 1];
        acc.push({
          ...row,
          upload: row.upload + (last?.upload || 0),
          download: row.download + (last?.download || 0),
          total: row.total + (last?.total || 0),
          samples: row.samples + (last?.samples || 0)
        });
        return acc;
      }, [])
    : chartDataFilled;

  const chartDataSorted = [...chartData].sort((a, b) => (a.key > b.key ? 1 : -1));

  const handleExportCsv = () => {
    const headers = ['Periodo', 'Upload', 'Download', 'Total', 'Amostras'];
      const rows = (series?.series || []).map((row) => [
        row.bucketLabel || format(new Date(row.bucket), 'yyyy-MM-dd HH:mm:ss'),
        toNumber(row.totalUploaded),
        toNumber(row.totalDownloaded),
        toNumber(row.totalTraffic),
        row.samples
      ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-queues-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user && !['GLOBAL_ADMIN', 'COMPANY_MANAGER'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Relatorios (Queues)</h1>
          <p className="text-sm text-gray-500">{periodLabel}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={accumulate}
              onChange={(e) => setAccumulate(e.target.checked)}
            />
            Acumulado
          </label>
          <button
            onClick={handleExportCsv}
            disabled={!series || (series.series || []).length === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:text-gray-400"
          >
            <Download size={16} />
            Exportar CSV
          </button>
          <button
            onClick={fetchReports}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:bg-gray-400"
          >
            <RefreshCw size={16} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 text-gray-700 mb-4">
          <Filter size={16} />
          <h2 className="text-sm font-semibold uppercase tracking-wide">Filtros</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Periodo</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="hour">Hora</option>
              <option value="day">Dia</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
              <option value="year">Ano</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Vessel</label>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Selecione uma filial</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.company?.name ? `${branch.company.name} - ` : ''}{branch.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Queue (usuario)</label>
            <select
              value={queueName}
              onChange={(e) => setQueueName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Todas</option>
              {queues.map((queue) => (
                <option key={queue} value={queue}>
                  {queue}
                </option>
              ))}
            </select>
          </div>

          {period === 'custom' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Inicio</label>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Fim</label>
                <div className="relative">
                  <Calendar size={14} className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total hora atual</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalsByPeriod.hour || 0)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total hoje</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalsByPeriod.day || 0)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total mes atual</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalsByPeriod.month || 0)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total ano atual</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalsByPeriod.year || 0)}</p>
        </div>
      </div>

      {!branchId && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Selecione uma filial para carregar os relatórios. Isso evita consultas pesadas em toda a base e deixa a tela muito mais rápida.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total trafego (filtro)</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalTraffic)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Upload (filtro)</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalUploaded)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Download (filtro)</p>
          <p className="text-2xl font-semibold text-gray-900">{formatBytes(totalDownloaded)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500">Coletas (amostras)</p>
          <p className="text-2xl font-semibold text-gray-900">{totalSamples}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Activity size={16} className="text-gray-700" />
            <h3 className="text-sm font-semibold text-gray-800">Evolucao do trafego</h3>
          </div>
          <div className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartDataSorted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatBytes(value as number)}
                />
                <Tooltip
                  formatter={(value: number) => formatBytes(value)}
                  labelFormatter={(label) => `Periodo: ${label}`}
                />
                <Line
                  type="linear"
                  dataKey="total"
                  stroke="#111827"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Activity size={16} className="text-gray-700" />
            <h3 className="text-sm font-semibold text-gray-800">Upload vs Download</h3>
          </div>
          <div className="p-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartDataSorted}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => formatBytes(value as number)} />
                <Tooltip formatter={(value: number) => formatBytes(value)} />
                <Legend />
                <Bar dataKey="upload" stackId="a" fill="#2563eb" name="Upload" />
                <Bar dataKey="download" stackId="a" fill="#93c5fd" name="Download" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <Activity size={16} className="text-gray-700" />
          <h3 className="text-sm font-semibold text-gray-800">Totais por periodo</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Periodo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upload</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Download</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amostras</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(series?.series || []).map((row) => (
                <tr key={row.bucket} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800">
                    {row.bucketLabel || format(new Date(row.bucket), 'yyyy-MM-dd HH:mm')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(toNumber(row.totalUploaded))}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(toNumber(row.totalDownloaded))}</td>
                  <td className="px-4 py-3 text-sm text-gray-800">{formatBytes(toNumber(row.totalTraffic))}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{row.samples}</td>
                </tr>
              ))}
              {(series?.series?.length || 0) === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                    Sem dados para o periodo selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <GitBranch size={16} className="text-gray-700" />
          <h3 className="text-sm font-semibold text-gray-800">Agrupado por branch</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vessel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upload</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Download</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amostras</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {branchSummary.map((row) => (
                <tr key={row.branchId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-800">{row.branchName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(toNumber(row.totalUploaded))}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(toNumber(row.totalDownloaded))}</td>
                  <td className="px-4 py-3 text-sm text-gray-800">{formatBytes(toNumber(row.totalTraffic))}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{row.samples}</td>
                </tr>
              ))}
              {branchSummary.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                    Sem dados para o periodo selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Reports;
